// Sistema inteligente de preload de rotas baseado no comportamento do usuário

interface RouteConfig {
  path: string;
  priority: number;
  preloadCondition?: () => boolean;
  loader: () => Promise<any>;
}

class RoutePreloader {
  private preloadQueue: Map<string, RouteConfig> = new Map();
  private loadedRoutes: Set<string> = new Set();
  private userBehavior: Map<string, number> = new Map();
  private currentRoute: string = '';

  constructor() {
    this.initializeUserTracking();
  }

  private initializeUserTracking() {
    // Rastrear tempo na página para prever próximas rotas
    let startTime = Date.now();
    
    const trackRouteTime = () => {
      const timeSpent = Date.now() - startTime;
      if (this.currentRoute) {
        const currentTime = this.userBehavior.get(this.currentRoute) || 0;
        this.userBehavior.set(this.currentRoute, currentTime + timeSpent);
      }
      startTime = Date.now();
    };

    window.addEventListener('beforeunload', trackRouteTime);
    window.addEventListener('popstate', trackRouteTime);
  }

  registerRoute(config: RouteConfig) {
    this.preloadQueue.set(config.path, config);
  }

  setCurrentRoute(path: string) {
    this.currentRoute = path;
    this.preloadByContext(path);
  }

  private preloadByContext(currentPath: string) {
    const routes = this.getContextualRoutes(currentPath);
    routes.forEach(route => {
      this.preloadRoute(route.path);
    });
  }

  private getContextualRoutes(currentPath: string): RouteConfig[] {
    const contextMap: Record<string, string[]> = {
      '/inicio': ['/dashboard', '/estoque', '/vendas'],
      '/dashboard': ['/vendas', '/estoque', '/clientes'],
      '/estoque': [],
      '/vendas': [],
      '/clientes': [],
      '/financeiro': ['/financeiro/movimentacoes', '/financeiro/relatorios'],
    };

    const relatedPaths = contextMap[currentPath] || [];
    return relatedPaths
      .map(path => this.preloadQueue.get(path))
      .filter(Boolean) as RouteConfig[];
  }

  async preloadRoute(path: string) {
    const route = this.preloadQueue.get(path);
    
    if (!route || this.loadedRoutes.has(path)) {
      return;
    }

    // Verificar condição de preload se existir
    if (route.preloadCondition && !route.preloadCondition()) {
      return;
    }

    try {
      // Preload com delay para não interferir na experiência atual
      await new Promise(resolve => setTimeout(resolve, 100));
      await route.loader();
      this.loadedRoutes.add(path);
      
      console.log(`✅ Preloaded route: ${path}`);
    } catch (error) {
      console.warn(`⚠️ Failed to preload route: ${path}`, error);
    }
  }

  preloadByPriority() {
    const sortedRoutes = Array.from(this.preloadQueue.values())
      .sort((a, b) => b.priority - a.priority);

    sortedRoutes.forEach(route => {
      if (!this.loadedRoutes.has(route.path)) {
        this.preloadRoute(route.path);
      }
    });
  }

  // Preload baseado em idle time
  preloadOnIdle() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        this.preloadByPriority();
      });
    } else {
      // Fallback para navegadores sem requestIdleCallback
      setTimeout(() => {
        this.preloadByPriority();
      }, 2000);
    }
  }

  // Preload baseado em hover (para links)
  preloadOnHover(path: string) {
    if (!this.loadedRoutes.has(path)) {
      setTimeout(() => {
        this.preloadRoute(path);
      }, 50); // Pequeno delay para evitar preloads desnecessários
    }
  }

  getLoadedRoutes(): string[] {
    return Array.from(this.loadedRoutes);
  }

  getUserBehavior(): Record<string, number> {
    return Object.fromEntries(this.userBehavior);
  }
}

// Instância global do preloader
export const routePreloader = new RoutePreloader();

// Hook para usar o preloader
export const useRoutePreloader = () => {
  const preloadOnHover = (path: string) => {
    routePreloader.preloadOnHover(path);
  };

  const setCurrentRoute = (path: string) => {
    routePreloader.setCurrentRoute(path);
  };

  return {
    preloadOnHover,
    setCurrentRoute,
    getLoadedRoutes: () => routePreloader.getLoadedRoutes(),
    getUserBehavior: () => routePreloader.getUserBehavior(),
  };
};

// Configurações de rotas para preload
export const configureRoutePreloading = () => {
  // Registrar todas as rotas com suas configurações
  const routes: RouteConfig[] = [
    {
      path: '/dashboard',
      priority: 10,
      loader: () => import('../pages/Dashboard'),
    },
    {
      path: '/estoque',
      priority: 9,
      loader: () => import('../pages/Inventory'),
    },
    {
      path: '/vendas',
      priority: 9,
      loader: () => import('../pages/SalesList'),
    },
    {
      path: '/pessoas',
      priority: 8,
      loader: () => import('../components/pessoas/dashboard/PessoaDashboard'),
    },
    {
      path: '/financeiro',
      priority: 7,
      loader: () => import('../pages/Financeiro'),
    },
    {
      path: '/vendas/nova',
      priority: 6,
      preloadCondition: () => {
        // Só preload se o usuário tem permissão para criar vendas
        return true; // Implementar lógica de permissão se necessário
      },
      loader: () => import('../pages/NewSale'),
    },
    {
      path: '/estoque/novo',
      priority: 5,
      loader: () => import('../pages/NewVehicle'),
    },
  ];

  routes.forEach(route => {
    routePreloader.registerRoute(route);
  });

  // Iniciar preload inteligente após 3 segundos
  setTimeout(() => {
    routePreloader.preloadOnIdle();
  }, 3000);
};